import React, { useEffect, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import { Icon } from "./brand";

/**
 * Toast — non-blocking notifications.
 *
 * Usage:
 *   import { toast } from "./Toast";
 *   toast.success("Client archived");
 *   toast.error("Something went wrong");
 *   toast.info("Saved");
 *
 * Toasts stack vertically, auto-dismiss after 4s, can be clicked to dismiss early.
 */

type ToastKind = "success" | "error" | "info";

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
  durationMs?: number;
  replaceKey?: string;
}

const STYLES: Record<ToastKind, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: "var(--positive-bg)", border: "#BBF7D0", text: "#166534", icon: "check" },
  error:   { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B", icon: "alert" },
  info:    { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E3A8A", icon: "info" },
};

let listeners: ((items: ToastItem[]) => void)[] = [];
let items: ToastItem[] = [];
let nextId = 1;

function notify() {
  listeners.forEach(l => l([...items]));
}

function push(kind: ToastKind, message: string, action?: ToastAction, durationMs: number = 4000, replaceKey?: string) {
  const id = nextId++;
  if (replaceKey) items = items.filter(i => i.replaceKey !== replaceKey);
  items.push({ id, kind, message, action, durationMs, replaceKey });
  notify();
  setTimeout(() => {
    items = items.filter(i => i.id !== id);
    notify();
  }, durationMs);
}

function dismiss(id: number) {
  items = items.filter(i => i.id !== id);
  notify();
}

function ToastContainer() {
  const [list, setList] = useState<ToastItem[]>([]);
  useEffect(() => {
    listeners.push(setList);
    return () => { listeners = listeners.filter(l => l !== setList); };
  }, []);

  return (
    <div className="mp-toast-stack" style={{
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: 9998,
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      pointerEvents: "none",
    }}>
      <style>{`
        @keyframes toastSlide { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        /* Phones: a right-anchored 260-400px toast ran off the left edge.
           Span the width with a gutter and sit above the home indicator. */
        @media (max-width: 480px) {
          .mp-toast-stack { left: 12px !important; right: 12px !important;
            bottom: max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px)) !important; }
          .mp-toast-stack > div { min-width: 0 !important; max-width: none !important; }
        }
      `}</style>
      {list.map(item => {
        const s = STYLES[item.kind];
        return (
          <div
            key={item.id}
            onClick={() => dismiss(item.id)}
            style={{
              pointerEvents: "auto",
              cursor: "pointer",
              minWidth: "260px",
              maxWidth: "400px",
              padding: "12px 16px",
              background: s.bg,
              border: `1px solid ${s.border}`,
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              fontFamily: "inherit",
              fontSize: "13px",
              color: s.text,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              animation: "toastSlide 0.2s ease",
            }}
          >
            <Icon name={s.icon as any} size={16} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{item.message}</span>
            {item.action && (
              <button
                onClick={(e) => { e.stopPropagation(); item.action!.onClick(); dismiss(item.id); }}
                style={{
                  background: "transparent",
                  border: `1px solid ${s.border}`,
                  color: s.text,
                  fontWeight: 700,
                  fontSize: "12px",
                  padding: "4px 10px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {item.action.label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Mount the container once into the body when first imported
let mounted = false;
function ensureMounted() {
  if (mounted) return;
  if (typeof document === "undefined") return; // SSR safety
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root: Root = createRoot(container);
  root.render(<ToastContainer />);
  mounted = true;
}

interface ToastOpts {
  action?: ToastAction;
  /** Override the default 4-second auto-dismiss. Use longer windows
   * for toasts that carry an Undo action — the user needs time to
   * actually click. */
  durationMs?: number;
  /** A newer toast with the same key replaces the one showing instead of
   * stacking under it. */
  replaceKey?: string;
}

export const toast = {
  success: (message: string, opts?: ToastOpts) => { ensureMounted(); push("success", message, opts?.action, opts?.durationMs, opts?.replaceKey); },
  error:   (message: string, opts?: ToastOpts) => { ensureMounted(); push("error",   message, opts?.action, opts?.durationMs, opts?.replaceKey); },
  info:    (message: string, opts?: ToastOpts) => { ensureMounted(); push("info",    message, opts?.action, opts?.durationMs, opts?.replaceKey); },
};
